<template>
  <textarea
    ref="textarea"
    class="block w-full h-full px-[1.5rem] py-[24px] border border-t-0 border-gray-200 rounded-b-md focus:outline-none resize-none text-[14px] text-[#1F2937]"
    style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;"
    :value="value"
    v-bind="$attrs"
    @input="$emit('input', $event.target.value)"
    @paste="handlePaste"
    @keydown.enter="handleEnter"
    @drop.prevent="handleDrop"
    @dragover.prevent
  />
</template>

<script>
export default {
  name: 'EditorTextarea',
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  methods: {
    /**
     * Handles the Enter keydown event to support auto-indentation and list continuation.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    handleEnter(event) {
      if (event.shiftKey) return
      const el = this.$refs.textarea
      const {
        selectionStart: start, selectionEnd: end, value: text,
      } = el
      const lineStart = text.lastIndexOf('\n', start - 1) + 1

      const match = text.substring(lineStart, start).match(/^(\s*)(?:([-*+])|(\d+)\.)\s+(.*)$/)
      if (match) {
        event.preventDefault()
        const [, indent, bullet, number, content] = match
        const isEmpty = !content.trim()
        let insertStr = '\n'

        if (!isEmpty) {
          insertStr += bullet ? `${indent}${bullet} ` : `${indent}${parseInt(number, 10) + 1}. `
        }

        el.focus()
        if (isEmpty) {
          el.setSelectionRange(lineStart, start)
          document.execCommand('insertText', false, '\n')
        } else {
          el.setSelectionRange(start, end)
          document.execCommand('insertText', false, insertStr)
        }

        this.$nextTick(() => {
          el.focus()
          const pos = isEmpty ? lineStart + 1 : start + insertStr.length
          el.setSelectionRange(pos, pos)
        })
      }
    },
    /**
     * Handles the paste event to intercept image pasting.
     * @param {ClipboardEvent} event - The clipboard event.
     */
    handlePaste(event) {
      const items = Array.from((event.clipboardData || window.clipboardData).items)
      const images = items.filter(i => i.type.startsWith('image'))
      if (images.length > 0) {
        event.preventDefault()
        images.forEach(img => {
          this.$emit('image-pasted', img.getAsFile())
        })
      }
    },
    /**
     * Handles the drop event to intercept image dropping.
     * @param {DragEvent} event - The drag event.
     */
    handleDrop(event) {
      const items = Array.from(event.dataTransfer.files)
      const images = items.filter(file => file.type.startsWith('image'))
      if (images.length > 0) {
        event.preventDefault()
        images.forEach(img => {
          this.$emit('image-pasted', img)
        })
      }
    },
    /**
     * Checks whether the current cursor position is inside a table line/cell.
     * @returns {boolean} True if cursor is inside a table line, false otherwise.
     */
    isCursorInTable() {
      const el = this.$refs.textarea
      if (!el) return false
      const lineStart = el.value.lastIndexOf('\n', el.selectionStart - 1) + 1
      return /^\s*\|/.test(el.value.slice(lineStart, lineStart + 100))
    },
    /**
     * Inserts text at the current cursor position or wraps the selected text.
     * @param {string} prefix - Text to insert before the selection.
     * @param {string} [suffix=''] - Text to insert after the selection.
     * @param {boolean} [isBlock=false] - Ensures the text is inserted on a new line if true.
     */
    insertText(prefix, suffix = '', isBlock = false) {
      const el = this.$refs.textarea
      const { selectionStart: start, selectionEnd: end, value: text } = el

      let cleanPrefix = prefix
      let cleanSuffix = suffix
      let cleanIsBlock = isBlock

      if (this.isCursorInTable()) {
        if (cleanPrefix.includes('```')) { cleanPrefix = ''; cleanSuffix = ''; cleanIsBlock = false }
        cleanPrefix = cleanPrefix.replace(/^!\[.*?\]\((.*?)\)$/, '$1')
      }

      const selectedText = text.substring(start, end)
      const preNewline = cleanIsBlock && start > 0 && text[start - 1] !== '\n' ? '\n' : ''

      const isOrdered = /^\d+\.\s*$/.test(cleanPrefix)
      const isList = /^([-*+])\s*$/.test(cleanPrefix) || isOrdered

      let formattedText = cleanPrefix + selectedText
      let selStart = start + preNewline.length + cleanPrefix.length
      let selEnd = end + preNewline.length + cleanPrefix.length

      if (isList && selectedText) {
        let idx = 1
        const hasNewline = selectedText.endsWith('\n')
        const clean = hasNewline ? selectedText.slice(0, -1) : selectedText
        const formatted = clean.split('\n').map(line => {
          if (!line.trim()) return line
          const itemPrefix = isOrdered ? `${idx}. ` : cleanPrefix
          if (isOrdered) idx += 1
          return `${itemPrefix}${line.trimStart().replace(/^(?:[-*+]|\d+\.)\s+/, '')}`
        }).join('\n') + (hasNewline ? '\n' : '')

        formattedText = formatted
        selStart = start + preNewline.length
        selEnd = selStart + formatted.length
      }

      const replacement = preNewline + formattedText + cleanSuffix

      el.focus()
      el.setSelectionRange(start, end)

      if (!document.execCommand('insertText', false, replacement)) {
        this.$emit('input', text.substring(0, start) + replacement + text.substring(end))
      }

      this.$nextTick(() => {
        el.focus()
        el.setSelectionRange(selStart, selEnd)
      })
    },
  },
}
</script>
